import React, { useState } from 'react';
import { Gift, Heart, Clock, Camera, Home, Users, Book, Star, Shield } from 'lucide-react';

const ForGrandparentsPage = ({ setCurrentPage }) => {
  const [selectedCategory, setSelectedCategory] = useState('all');

  const giftCategories = [
    { id: 'comfort', name: 'Komfort & Helse', icon: Heart, color: 'pink' },
    { id: 'memories', name: 'Minner & Familie', icon: Camera, color: 'blue' },
    { id: 'hobbies', name: 'Hobbyer & Interesser', icon: Book, color: 'green' },
    { id: 'practical', name: 'Praktisk hjelp', icon: Home, color: 'purple' },
    { id: 'social', name: 'Sosialt & Aktivitet', icon: Users, color: 'orange' }
  ];

  return (
    <div className="min-h-screen bg-secondary-50">
      {/* Hero Section */}
      <section className="bg-gradient-to-br from-warning-50 via-orange-50 to-error-50 py-16">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto text-center">
            <div className="text-8xl mb-6">👴</div>
            <h1 className="text-5xl font-bold text-secondary-800 mb-4">Gaver til besteforeldre</h1>
            <p className="text-xl text-secondary-600 max-w-2xl mx-auto mb-8">
              Vis takknemlighet og kjærlighet til de som har gitt så mye. Finn gaver som feirer 
              deres visdom, bringer komfort og styrker familiebåndene.
            </p>
            <button
              onClick={() => setCurrentPage('gift-finder')}
              className="px-8 py-4 bg-gradient-to-r from-primary-500 to-purple-600 text-white rounded-lg hover:from-primary-600 hover:to-purple-700 transition-all transform hover:scale-105 shadow-lg font-semibold"
            >
              <Gift className="w-5 h-5 inline mr-2" />
              Finn gaver til besteforeldre
            </button>
          </div>
        </div>
      </section>

      {/* Understanding Grandparents */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-4">
          <div className="max-w-6xl mx-auto">
            <h2 className="text-3xl font-bold text-center text-secondary-800 mb-12">
              Hva gjør besteforeldre spesielle
            </h2>
            
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-12">
              <div className="bg-gradient-to-br from-warning-50 to-orange-50 rounded-lg p-6 border border-warning-100">
                <Heart className="w-12 h-12 text-warning-500 mb-4" />
                <h3 className="text-xl font-semibold text-secondary-800 mb-3">Livets visdom</h3>
                <p className="text-secondary-600">
                  Besteforeldre har opplevd så mye og har verdifull visdom å dele. 
                  De verdsetter gaver som anerkjenner deres erfaring og bidrag til familien.
                </p>
              </div>

              <div className="bg-gradient-to-br from-orange-50 to-error-50 rounded-lg p-6 border border-orange-100">
                <Users className="w-12 h-12 text-orange-500 mb-4" />
                <h3 className="text-xl font-semibold text-secondary-800 mb-3">Familie kommer først</h3>
                <p className="text-secondary-600">
                  For de fleste besteforeldre er familie det viktigste. Gaver som styrker 
                  familiebånd eller bevarer minner blir dypt verdsatt.
                </p>
              </div>

              <div className="bg-gradient-to-br from-error-50 to-warning-50 rounded-lg p-6 border border-error-100">
                <Clock className="w-12 h-12 text-error-500 mb-4" />
                <h3 className="text-xl font-semibold text-secondary-800 mb-3">Tid er verdifull</h3>
                <p className="text-secondary-600">
                  De forstår verdien av tid og øyeblikk. Gaver som skaper kvalitetstid 
                  eller gjør hverdagen enklere blir ofte høyt verdsatt.
                </p>
              </div>
            </div>

            <div className="bg-success-50 border border-success-200 rounded-lg p-6">
              <div className="flex items-start space-x-4">
                <Star className="w-6 h-6 text-success-600 mt-1 flex-shrink-0" />
                <div>
                  <h3 className="text-lg font-semibold text-success-800 mb-2">Besteforeldre gir alltid</h3>
                  <p className="text-success-700">
                    De har brukt livet sitt på å gi - til barn, barnebarn, samfunn. 
                    Det er på tide at noen gir tilbake til dem. En gjennomtenkt gave kan vise 
                    hvor mye deres kjærlighet og omsorg har betydd gjennom årene.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Gift Categories */}
      <section className="py-16 bg-secondary-50">
        <div className="container mx-auto px-4">
          <div className="max-w-6xl mx-auto">
            <h2 className="text-3xl font-bold text-center text-secondary-800 mb-12">
              Gavekategorier for besteforeldre
            </h2>

            {/* Category Filter */}
            <div className="flex flex-wrap justify-center gap-3 mb-12">
              <button
                onClick={() => setSelectedCategory('all')}
                className={`px-4 py-2 rounded-lg border transition-colors ${
                  selectedCategory === 'all'
                    ? 'bg-warning-500 text-white border-warning-500'
                    : 'bg-white text-secondary-700 border-secondary-300 hover:border-warning-300'
                }`}
              >
                Alle kategorier
              </button>
              {giftCategories.map((category) => (
                <button
                  key={category.id}
                  onClick={() => setSelectedCategory(category.id)}
                  className={`px-4 py-2 rounded-lg border transition-colors flex items-center space-x-2 ${
                    selectedCategory === category.id
                      ? `bg-${category.color}-500 text-white border-${category.color}-500`
                      : 'bg-white text-secondary-700 border-secondary-300 hover:border-secondary-400'
                  }`}
                >
                  <category.icon className="w-4 h-4" />
                  <span>{category.name}</span>
                </button>
              ))}
            </div>

            {/* Category Cards */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
              {/* Comfort & Health */}
              <div className={`bg-white rounded-lg shadow-sm p-8 border border-secondary-100 ${
                selectedCategory === 'all' || selectedCategory === 'comfort' ? 'block' : 'hidden'
              }`}>
                <div className="flex items-center mb-6">
                  <Heart className="w-8 h-8 text-primary-500 mr-3" />
                  <h3 className="text-2xl font-semibold text-secondary-800">Komfort & Helse</h3>
                </div>
                <div className="space-y-4">
                  <div className="border-l-4 border-primary-200 pl-4">
                    <h4 className="font-semibold text-secondary-800 mb-2">🛏️ Hvile og komfort (200-800 kr)</h4>
                    <ul className="text-sm text-secondary-600 space-y-1">
                      <li>• Ortopediske puter og støtteputer</li>
                      <li>• Varme pledd og kashmere-tepper</li>
                      <li>• Ergonomiske sitteputere</li>
                      <li>• Massage-apparater og varmeposer</li>
                      <li>• Gode lesestøtter og øyenmasker</li>
                    </ul>
                  </div>
                  
                  <div className="border-l-4 border-primary-200 pl-4">
                    <h4 className="font-semibold text-secondary-800 mb-2">💊 Helse og velvære (150-600 kr)</h4>
                    <ul className="text-sm text-secondary-600 space-y-1">
                      <li>• Medisindosetter og pillorganisatorer</li>
                      <li>• Fotvarmere og varme tøfler</li>
                      <li>• Aromaterapi og avslappende duftlys</li>
                      <li>• Helse-apper og fitness-trackere for seniorer</li>
                      <li>• Øyekompress og søvnmasker</li>
                    </ul>
                  </div>
                </div>
                
                <div className="mt-6 bg-primary-50 p-3 rounded text-xs text-primary-700">
                  💝 Tips: Komfort-gaver viser at du bryr deg om deres daglige velvære
                </div>
              </div>

              {/* Memories & Family */}
              <div className={`bg-white rounded-lg shadow-sm p-8 border border-secondary-100 ${
                selectedCategory === 'all' || selectedCategory === 'memories' ? 'block' : 'hidden'
              }`}>
                <div className="flex items-center mb-6">
                  <Camera className="w-8 h-8 text-accent-500 mr-3" />
                  <h3 className="text-2xl font-semibold text-secondary-800">Minner & Familie</h3>
                </div>
                <div className="space-y-4">
                  <div className="border-l-4 border-accent-200 pl-4">
                    <h4 className="font-semibold text-secondary-800 mb-2">📸 Familie-minner (100-500 kr)</h4>
                    <ul className="text-sm text-secondary-600 space-y-1">
                      <li>• Digitale fotorammer som oppdateres automatisk</li>
                      <li>• Personaliserte fotoalbum og scrapbooks</li>
                      <li>• Familie-kalendere med bilder</li>
                      <li>• Canvas-trykk av familiefoto</li>
                      <li>• Video-sammenstillinger av familiehøydepunkter</li>
                    </ul>
                  </div>
                  
                  <div className="border-l-4 border-accent-200 pl-4">
                    <h4 className="font-semibold text-secondary-800 mb-2">📱 Teknologi for kontakt (300-1000 kr)</h4>
                    <ul className="text-sm text-secondary-600 space-y-1">
                      <li>• Enkle tablets for videochat</li>
                      <li>• Smart høyttalere for familiekontakt</li>
                      <li>• Seniorvennlige mobiltelefoner</li>
                      <li>• Video-telefoner med store knapper</li>
                      <li>• Familiedeling og foto-sharing tjenester</li>
                    </ul>
                  </div>
                </div>
                
                <div className="mt-6 bg-accent-50 p-3 rounded text-xs text-accent-700">
                  📷 Tips: Minner-gaver knytter generasjoner sammen og bevarer familiehistorie
                </div>
              </div>

              {/* Hobbies & Interests */}
              <div className={`bg-white rounded-lg shadow-sm p-8 border border-secondary-100 ${
                selectedCategory === 'all' || selectedCategory === 'hobbies' ? 'block' : 'hidden'
              }`}>
                <div className="flex items-center mb-6">
                  <Book className="w-8 h-8 text-success-500 mr-3" />
                  <h3 className="text-2xl font-semibold text-secondary-800">Hobbyer & Interesser</h3>
                </div>
                <div className="space-y-4">
                  <div className="border-l-4 border-success-200 pl-4">
                    <h4 className="font-semibold text-secondary-800 mb-2">📚 Lesing og læring (100-400 kr)</h4>
                    <ul className="text-sm text-secondary-600 space-y-1">
                      <li>• Store-print bøker og audiobook-abonnement</li>
                      <li>• E-readere med justerbar skriftstørrelse</li>
                      <li>• Puslespill med store brikker</li>
                      <li>• Kryss og tvers magasiner</li>
                      <li>• Historiske og biografiske bøker</li>
                    </ul>
                  </div>
                  
                  <div className="border-l-4 border-success-200 pl-4">
                    <h4 className="font-semibold text-secondary-800 mb-2">🌱 Kreativitet og håndverk (150-600 kr)</h4>
                    <ul className="text-sm text-secondary-600 space-y-1">
                      <li>• Strikkesett og garn i mykeplastic</li>
                      <li>• Enkle malingsett og akvareller</li>
                      <li>• Hagearbeid inne med krukker og frø</li>
                      <li>• Scrapbooking og minnebok-materialer</li>
                      <li>• Fuglemating og fugletitting-utstyr</li>
                    </ul>
                  </div>
                </div>
                
                <div className="mt-6 bg-success-50 p-3 rounded text-xs text-success-700">
                  🎨 Tips: Hobby-gaver gir meningsfylt tidsfordriv og følelse av formål
                </div>
              </div>

              {/* Practical Help */}
              <div className={`bg-white rounded-lg shadow-sm p-8 border border-secondary-100 ${
                selectedCategory === 'all' || selectedCategory === 'practical' ? 'block' : 'hidden'
              }`}>
                <div className="flex items-center mb-6">
                  <Home className="w-8 h-8 text-purple-500 mr-3" />
                  <h3 className="text-2xl font-semibold text-secondary-800">Praktisk hjelp</h3>
                </div>
                <div className="space-y-4">
                  <div className="border-l-4 border-purple-200 pl-4">
                    <h4 className="font-semibold text-secondary-800 mb-2">🏠 Hjemme-assistanse (200-800 kr)</h4>
                    <ul className="text-sm text-secondary-600 space-y-1">
                      <li>• Robot-støvsuger for enkelt renhold</li>
                      <li>• Timere og påminnere for medisiner</li>
                      <li>• Ergonomiske kjøkkenredskaper</li>
                      <li>• Automatiske belysningsløsninger</li>
                      <li>• Hjelp med daglige gjøremål (service-gavekort)</li>
                    </ul>
                  </div>
                  
                  <div className="border-l-4 border-purple-200 pl-4">
                    <h4 className="font-semibold text-secondary-800 mb-2">🚗 Mobilitet og sikkerhet (150-700 kr)</h4>
                    <ul className="text-sm text-secondary-600 space-y-1">
                      <li>• Trygghetsalarmer og nødanrop</li>
                      <li>• GPS-trackere for sikkerhet</li>
                      <li>• Transporttjenester og taxi-kort</li>
                      <li>• Handlekurver og mobilitetshjelpemidler</li>
                      <li>• Belysning med bevegelsessensorer</li>
                    </ul>
                  </div>
                </div>
                
                <div className="mt-6 bg-purple-50 p-3 rounded text-xs text-purple-700">
                  🔧 Tips: Praktiske gaver viser omtanke for deres daglige behov og sikkerhet
                </div>
              </div>

              {/* Social & Activity */}
              <div className={`bg-white rounded-lg shadow-sm p-8 border border-secondary-100 ${
                selectedCategory === 'all' || selectedCategory === 'social' ? 'block' : 'hidden'
              }`}>
                <div className="flex items-center mb-6">
                  <Users className="w-8 h-8 text-warning-500 mr-3" />
                  <h3 className="text-2xl font-semibold text-secondary-800">Sosialt & Aktivitet</h3>
                </div>
                <div className="space-y-4">
                  <div className="border-l-4 border-warning-200 pl-4">
                    <h4 className="font-semibold text-secondary-800 mb-2">👥 Sosiale opplevelser (200-1000 kr)</h4>
                    <ul className="text-sm text-secondary-600 space-y-1">
                      <li>• Teater og konsertbilletter</li>
                      <li>• Seniorgruppe-aktiviteter og kurs</li>
                      <li>• Restaurantbesøk og afternoon tea</li>
                      <li>• Gruppeturarrangementer</li>
                      <li>• Bridge, bingo og andre sosiale spill</li>
                    </ul>
                  </div>
                  
                  <div className="border-l-4 border-warning-200 pl-4">
                    <h4 className="font-semibold text-secondary-800 mb-2">🚶 Aktivitet og helse (100-500 kr)</h4>
                    <ul className="text-sm text-secondary-600 space-y-1">
                      <li>• Seniorvennlige treningsutstyr</li>
                      <li>• Svømmehall og spa-besøk</li>
                      <li>• Gange og balansehjelpemidler</li>
                      <li>• Yoga og meditasjon for seniorer</li>
                      <li>• Naturtur og friluftsaktiviteter</li>
                    </ul>
                  </div>
                </div>
                
                <div className="mt-6 bg-warning-50 p-3 rounded text-xs text-warning-700">
                  👫 Tips: Sosiale gaver kan bekjempe ensomhet og holde dem aktive
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Special Considerations */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-3xl font-bold text-center text-secondary-800 mb-12">
              Spesielle hensyn for besteforeldre-gaver
            </h2>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
              <div className="bg-gradient-to-br from-success-50 to-emerald-50 rounded-lg p-6 border border-success-100">
                <Shield className="w-8 h-8 text-success-500 mb-4" />
                <h3 className="text-xl font-semibold text-success-800 mb-4">Sikkerhet og tilgjengelighet</h3>
                <ul className="space-y-2 text-success-700 text-sm">
                  <li>• Velg produkter med store, tydelige knapper</li>
                  <li>• Unngå kompliserte teknologiske løsninger</li>
                  <li>• Sjekk at produkter er lette å bruke og vedlikeholde</li>
                  <li>• Vurder deres fysiske begrensninger</li>
                  <li>• Sikre at gaver er trygge å bruke alene</li>
                </ul>
              </div>

              <div className="bg-gradient-to-br from-accent-50 to-indigo-50 rounded-lg p-6 border border-accent-100">
                <Clock className="w-8 h-8 text-accent-500 mb-4" />
                <h3 className="text-xl font-semibold text-accent-800 mb-4">Timing og levering</h3>
                <ul className="space-y-2 text-accent-700 text-sm">
                  <li>• Lever gaver personlig når mulig</li>
                  <li>• Planlegg besøk rundt gavegivingen</li>
                  <li>• Hjelp med oppsett av teknologiske gaver</li>
                  <li>• Tilby å lære dem hvordan ting fungerer</li>
                  <li>• Følg opp for å sikre at gaven brukes</li>
                </ul>
              </div>
            </div>

            <div className="mt-8 bg-warning-50 border border-warning-200 rounded-lg p-6">
              <div className="flex items-start space-x-4">
                <Heart className="w-6 h-6 text-warning-600 mt-1 flex-shrink-0" />
                <div>
                  <h3 className="text-lg font-semibold text-warning-800 mb-2">Den største gaven</h3>
                  <p className="text-warning-700">
                    Husk at for de fleste besteforeldre er den største gaven din tid og oppmerksomhet. 
                    Legg ved en hjertelig hilsen, ring for å høre hvordan de liker gaven, og bruk 
                    tid sammen med dem. Dette er ofte mer verdifullt enn selve gaven.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Intergenerational Gift Ideas */}
      <section className="py-16 bg-secondary-50">
        <div className="container mx-auto px-4">
          <div className="max-w-6xl mx-auto">
            <h2 className="text-3xl font-bold text-center text-secondary-800 mb-12">
              Gaver som bringer generasjoner sammen
            </h2>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
              <div className="bg-white rounded-lg p-6 border border-secondary-100">
                <h3 className="text-lg font-semibold text-secondary-800 mb-4 text-center">👨‍👩‍👧‍👦 Familie-aktiviteter</h3>
                <ul className="space-y-2 text-sm text-secondary-600">
                  <li>• Brettspill for alle aldre</li>
                  <li>• Puslespill familien kan jobbe med sammen</li>
                  <li>• Kokebok med familieoppskrifter</li>
                  <li>• Hage-prosjekter for besteforeldre og barnebarn</li>
                  <li>• Digital scrapbook hele familien kan bidra til</li>
                </ul>
              </div>

              <div className="bg-white rounded-lg p-6 border border-secondary-100">
                <h3 className="text-lg font-semibold text-secondary-800 mb-4 text-center">📚 Læring og deling</h3>
                <ul className="space-y-2 text-sm text-secondary-600">
                  <li>• Memoirs og selvbiografi-hjelpemidler</li>
                  <li>• Video-opptak utstyr for historiefortelling</li>
                  <li>• Språklæring for å snakke med barnebarn</li>
                  <li>• Hobby-utstyr de kan dele med yngre</li>
                  <li>• Kulturelle opplevelser for hele familien</li>
                </ul>
              </div>

              <div className="bg-white rounded-lg p-6 border border-secondary-100">
                <h3 className="text-lg font-semibold text-secondary-800 mb-4 text-center">💻 Teknologi-broer</h3>
                <ul className="space-y-2 text-sm text-secondary-600">
                  <li>• Enkle video-chat oppsett</li>
                  <li>• Delte foto-streams med familien</li>
                  <li>• Gaming som besteforeldre og barn kan spille</li>
                  <li>• Teknologi-kurs for seniorer</li>
                  <li>• Smart home-løsninger hele familien kan bruke</li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 bg-gradient-to-r from-warning-500 to-orange-600">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto text-center">
            <h2 className="text-3xl font-bold text-white mb-4">
              Klar til å vise takknemlighet til besteforeldre?
            </h2>
            <p className="text-xl text-warning-100 mb-8">
              Finn gaver som feirer deres visdom og viser hvor mye de betyr for familien
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <button
                onClick={() => setCurrentPage('gift-finder')}
                className="px-8 py-4 bg-white text-warning-600 rounded-lg hover:bg-secondary-100 transition-colors font-semibold shadow-lg"
              >
                <Gift className="w-5 h-5 inline mr-2" />
                Start gavefinneren
              </button>
              <button
                onClick={() => setCurrentPage('recipients')}
                className="px-8 py-4 border-2 border-white text-white rounded-lg hover:bg-white hover:text-warning-600 transition-colors font-semibold"
              >
                Se andre mottakere
              </button>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default ForGrandparentsPage;